import React from 'react';
import { FieldRadioButton, FieldSelect, FieldTextInput } from '../components';
import { useIntl } from 'react-intl';
import css from './BilingDetailsForm.module.css';
import getCountryCodes from '../translations/countryCodes';
import { useConfiguration } from '../context/configurationContext';
import * as validators from '../util/validators';
import classNames from 'classnames';

// Fields only: safe to embed in either account settings or the payment form.
const BillingDetailsFields = ({ prefix = '', idPrefix = '', checkout = false, entityType }) => {
  const intl = useIntl();
  const config = useConfiguration();
  const countryCodes = getCountryCodes(config.localization.locale);
  const required = validators.required(
    intl.formatMessage({ id: 'BillingDetailsForm.requiredField' })
  );
  const fieldName = name => `${prefix}${name}`;
  return (
    <>
      <fieldset className={css.entityTypeGroup}>
        <legend className={css.visuallyHidden}>
          {intl.formatMessage({ id: 'BillingDetailsForm.entityTypeLabel' })}
        </legend>
        <FieldRadioButton
          id={`${idPrefix}billing-entity-type-individual`}
          name={fieldName('entityType')}
          value="individual"
          label={intl.formatMessage({ id: 'BillingDetailsForm.entityTypeIndividual' })}
        />
        <FieldRadioButton
          id={`${idPrefix}billing-entity-type-company`}
          name={fieldName('entityType')}
          value="company"
          label={intl.formatMessage({ id: 'BillingDetailsForm.entityTypeCompany' })}
        />
      </fieldset>
      <div className={classNames(css.formRow, css.fullWidth)}>
        <FieldTextInput
          id={`${idPrefix}companyName`}
          name={fieldName('companyName')}
          type="text"
          label={intl.formatMessage({
            id: checkout
              ? entityType === 'company'
                ? 'ReceiptRequest.companyName'
                : 'ReceiptRequest.fullName'
              : 'BillingDetailsForm.companyNameLabel',
          })}
          placeholder={intl.formatMessage({
            id: 'BillingDetailsForm.companyNamePlaceholder',
          })}
          validate={checkout ? undefined : required}
          className={css.formFld}
        />
      </div>
      <div className={classNames(css.formRow, css.fullWidth)}>
        <FieldTextInput
          id={`${idPrefix}vatNumber`}
          name={fieldName('vatNumber')}
          type="text"
          label={intl.formatMessage({
            id:
              checkout && entityType === 'company'
                ? 'ReceiptRequest.taxId'
                : 'BillingDetailsForm.vatNumberLabel',
          })}
          placeholder={intl.formatMessage({ id: 'BillingDetailsForm.vatNumberPlaceholder' })}
          className={css.formFld}
        />
      </div>
      <div className={classNames(css.formRow, css.fullWidth)}>
        <FieldTextInput
          id={`${idPrefix}addressLine1`}
          name={fieldName('addressLine1')}
          type="text"
          label={`${intl.formatMessage({ id: 'BillingDetailsForm.addressLine1Label' })}${
            checkout ? ' *' : ''
          }`}
          aria-required={checkout || undefined}
          placeholder={intl.formatMessage({
            id: 'BillingDetailsForm.addressLine1Placeholder',
          })}
          validate={checkout ? undefined : required}
          className={css.formFld}
        />
      </div>
      <div className={classNames(css.formRow, css.fullWidth)}>
        <FieldTextInput
          id={`${idPrefix}addressLine2`}
          name={fieldName('addressLine2')}
          type="text"
          label={intl.formatMessage({ id: 'BillingDetailsForm.addressLine2Label' })}
          placeholder={intl.formatMessage({
            id: 'BillingDetailsForm.addressLine2Placeholder',
          })}
          className={css.formFld}
        />
      </div>

      <div className={css.formRow}>
        <FieldTextInput
          id={`${idPrefix}city`}
          name={fieldName('city')}
          type="text"
          label={`${intl.formatMessage({ id: 'BillingDetailsForm.cityLabel' })}${
            checkout ? ' *' : ''
          }`}
          aria-required={checkout || undefined}
          placeholder={intl.formatMessage({ id: 'BillingDetailsForm.cityPlaceholder' })}
          validate={checkout ? undefined : required}
          className={css.formFld}
        />

        <FieldTextInput
          id={`${idPrefix}postalCode`}
          name={fieldName('postalCode')}
          type="text"
          label={`${intl.formatMessage({ id: 'BillingDetailsForm.postalCodeLabel' })}${
            checkout ? ' *' : ''
          }`}
          aria-required={checkout || undefined}
          placeholder={intl.formatMessage({ id: 'BillingDetailsForm.postalCodePlaceholder' })}
          validate={checkout ? undefined : required}
          className={css.formFld}
        />
      </div>

      <div className={classNames(css.formRow, css.fullWidth)}>
        <FieldSelect
          id={`${idPrefix}country`}
          name={fieldName('country')}
          label={`${intl.formatMessage({ id: 'BillingDetailsForm.countryLabel' })}${
            checkout ? ' *' : ''
          }`}
          aria-required={checkout || undefined}
          className={css.formFld}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'BillingDetailsForm.countryPlaceholder' })}
          </option>
          {countryCodes.map(country => {
            return (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            );
          })}
        </FieldSelect>
      </div>
      {checkout && (
        <div className={classNames(css.formRow, css.fullWidth)}>
          <FieldTextInput
            id={`${idPrefix}email`}
            name={fieldName('email')}
            type="email"
            label={intl.formatMessage({ id: 'ReceiptRequest.email' })}
            className={css.formFld}
          />
        </div>
      )}
    </>
  );
};
export default BillingDetailsFields;
